import gsap from 'gsap';

type ItemCache = {
  width: number;
  left: number;
};

const MOBILE_GAP = 60;
const DESKTOP_GAP = 80;
const HOVER_SCALE = 2;
const HOVER_EASE = 0.18;
const TAP_THRESHOLD_PX = 10;

export default class UgcSection extends HTMLElement {
  private container: HTMLUListElement | null = null;
  private baseItems: HTMLLIElement[] = [];
  private items: HTMLLIElement[] = [];
  private fullItems: HTMLLIElement[] = [];

  private isAnimated = false;
  private isDragging = false;
  private isHovered = false;
  private didDragThisGesture = false;
  private mouseX = 0;
  private startDragX = 0;
  private pointerDownX = 0;
  private pointerDownY = 0;
  private targetDragX = 0;
  private currentDragX = 0;
  private lastFrameTime = 0;
  private animationFrameId: number | null = null;
  private activeFullItemIndex = -1;

  private dragXEase = 0.065;
  private dragXSpeed = 2;
  private scrollXSpeed = 1.5;
  private autoScrollSpeed = 0.4;
  private autoScrollDirection = -1;

  private itemsGap = DESKTOP_GAP;
  private itemWidth = 0;
  private itemContentWidth = 0;
  private mediaContentWidth = 0;
  private mediaContentHeight = 0;
  private maxLabelShiftX = 0;
  private maxLabelShiftY = 0;
  private itemsCache: ItemCache[] = [];
  private mediaScale: number[] = [];
  private hoverShift: number[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  private finePointerMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  private onUpdateBound = this.onUpdate.bind(this);
  private onDragStartBound = this.onDragStart.bind(this);
  private onDragBound = this.onDrag.bind(this);
  private onDragEndBound = this.onDragEnd.bind(this);
  private onWheelBound = this.onWheel.bind(this);
  private onReducedMotionChangeBound = this.onReducedMotionChange.bind(this);
  private onFinePointerChangeBound = this.onFinePointerChange.bind(this);
  private onMouseEnterBound = this.onMouseEnter.bind(this);
  private onMouseLeaveBound = this.onMouseLeave.bind(this);
  private onMouseMoveBound = this.onMouseMove.bind(this);
  private onItemClickBound = this.onItemClick.bind(this);
  private onDocumentPointerDownBound = this.onDocumentPointerDown.bind(this);

  private fullItemTimeline: GSAPTimeline | null = null;

  private get usesDockHover(): boolean {
    return this.finePointerMediaQuery.matches;
  }

  connectedCallback(): void {
    this.container = this.querySelector('[data-ugc-section-ref="container"]');
    this.baseItems = Array.from(this.querySelectorAll('[data-ugc-section-ref="items"]'));
    this.fullItems = Array.from(this.querySelectorAll('[data-ugc-section-ref="full-items"]'));
    this.items = [...this.baseItems];

    if (!this.container || this.baseItems.length === 0) return;

    this.syncItemsForViewport();
    this.addEventListeners();
    this.handleResize();
    this.lastFrameTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.onUpdateBound);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.hideFullItem();

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private addEventListeners(): void {
    this.container?.addEventListener('mousedown', this.onDragStartBound);
    window.addEventListener('mousemove', this.onDragBound);
    window.addEventListener('mouseup', this.onDragEndBound);

    this.container?.addEventListener('touchstart', this.onDragStartBound, { passive: true });
    window.addEventListener('touchmove', this.onDragBound, { passive: true });
    window.addEventListener('touchend', this.onDragEndBound);

    this.container?.addEventListener('wheel', this.onWheelBound, { passive: true });
    this.container?.addEventListener('mouseenter', this.onMouseEnterBound);
    this.container?.addEventListener('mouseleave', this.onMouseLeaveBound);
    this.container?.addEventListener('mousemove', this.onMouseMoveBound);
    this.reducedMotionMediaQuery.addEventListener('change', this.onReducedMotionChangeBound);
    this.finePointerMediaQuery.addEventListener('change', this.onFinePointerChangeBound);

    this.bindItemTapListeners();

    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    this.resizeObserver.observe(document.body);
  }

  private removeEventListeners(): void {
    this.container?.removeEventListener('mousedown', this.onDragStartBound);
    window.removeEventListener('mousemove', this.onDragBound);
    window.removeEventListener('mouseup', this.onDragEndBound);

    this.container?.removeEventListener('touchstart', this.onDragStartBound);
    window.removeEventListener('touchmove', this.onDragBound);
    window.removeEventListener('touchend', this.onDragEndBound);

    this.container?.removeEventListener('wheel', this.onWheelBound);
    this.container?.removeEventListener('mouseenter', this.onMouseEnterBound);
    this.container?.removeEventListener('mouseleave', this.onMouseLeaveBound);
    this.container?.removeEventListener('mousemove', this.onMouseMoveBound);
    this.reducedMotionMediaQuery.removeEventListener('change', this.onReducedMotionChangeBound);
    this.finePointerMediaQuery.removeEventListener('change', this.onFinePointerChangeBound);
    document.removeEventListener('pointerdown', this.onDocumentPointerDownBound, true);

    this.unbindItemTapListeners();
  }

  private bindItemTapListeners(): void {
    if (this.usesDockHover) return;

    for (const item of this.items) {
      item.addEventListener('click', this.onItemClickBound);
    }
  }

  private unbindItemTapListeners(): void {
    for (const item of this.items) {
      item.removeEventListener('click', this.onItemClickBound);
    }
  }

  private onReducedMotionChange(): void {
    this.lastFrameTime = performance.now();
  }

  private onFinePointerChange(): void {
    this.hideFullItem();
    this.unbindItemTapListeners();
    this.bindItemTapListeners();
    this.handleResize();
  }

  private resetHoverBuffers(): void {
    this.mediaScale = this.items.map(() => 1);
    this.hoverShift = this.items.map(() => 0);
  }

  private getMediaForItem(item: HTMLLIElement): HTMLDivElement | null {
    return item.querySelector('[data-ugc-section-ref="medias"]') as HTMLDivElement | null;
  }

  private getLabelForItem(item: HTMLLIElement): HTMLParagraphElement | null {
    return item.querySelector('[data-ugc-section-ref="labels"]') as HTMLParagraphElement | null;
  }

  private showFullItem(index: number): void {
    const fullItem = this.fullItems[index];
    if (!fullItem) return;

    this.fullItemTimeline?.kill();
    this.fullItemTimeline = gsap.timeline();

    for (const item of this.fullItems) {
      item.style.pointerEvents = 'none';
      if (item !== fullItem) {
        gsap.set(item, { autoAlpha: 0 });
      }
    }

    fullItem.style.pointerEvents = 'auto';
    this.fullItemTimeline.to(fullItem, {
      duration: 0,
      autoAlpha: 1,
      ease: 'power2.out',
    });

    this.activeFullItemIndex = index;
    document.addEventListener('pointerdown', this.onDocumentPointerDownBound, true);
  }

  private hideFullItem(): void {
    this.fullItemTimeline?.kill();
    this.fullItemTimeline = null;

    for (const fullItem of this.fullItems) {
      fullItem.style.pointerEvents = 'none';
      gsap.set(fullItem, { autoAlpha: 0 });
    }

    this.activeFullItemIndex = -1;
    document.removeEventListener('pointerdown', this.onDocumentPointerDownBound, true);
  }

  private onItemClick(event: MouseEvent): void {
    if (this.usesDockHover || this.didDragThisGesture) return;

    const item = event.currentTarget as HTMLLIElement;
    const index = Number.parseInt(item.dataset.index ?? '0', 10);
    if (Number.isNaN(index) || !this.fullItems[index]) return;

    event.stopPropagation();
    this.showFullItem(index);
  }

  private onDocumentPointerDown(event: PointerEvent): void {
    if (this.activeFullItemIndex < 0) return;

    const activeFullItem = this.fullItems[this.activeFullItemIndex];
    if (!activeFullItem) {
      this.hideFullItem();
      return;
    }

    const target = event.target as Node | null;
    if (target && activeFullItem.contains(target)) return;

    // Let item clicks switch fullItem instead of just closing.
    if (target instanceof Element && target.closest('[data-ugc-section-ref="items"]')) return;

    this.hideFullItem();
  }

  private onMouseEnter(event: MouseEvent): void {
    this.isHovered = true;
    this.mouseX = event.clientX;
    this.lastFrameTime = performance.now();
  }

  private onMouseLeave(): void {
    this.isHovered = false;
    this.lastFrameTime = performance.now();
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouseX = event.clientX;
  }

  private syncItemsForViewport(): void {
    if (!this.container || this.baseItems.length === 0) return;

    this.unbindItemTapListeners();

    this.container.querySelectorAll('[data-ugc-section-clone="true"]').forEach((item) => {
      item.remove();
    });

    const baseSetWidth = this.baseItems.reduce((width, item) => {
      // offsetWidth ignores CSS transforms (unlike getBoundingClientRect).
      return width + item.offsetWidth + this.itemsGap;
    }, 0);

    if (baseSetWidth <= 0) {
      this.items = [...this.baseItems];
      this.resetHoverBuffers();
      this.bindItemTapListeners();
      return;
    }

    const minimumTrackWidth = window.innerWidth * 2;
    const minimumSets = Math.max(2, Math.ceil(minimumTrackWidth / baseSetWidth));

    for (let setIndex = 1; setIndex < minimumSets; setIndex += 1) {
      this.baseItems.forEach((baseItem) => {
        const clone = baseItem.cloneNode(true) as HTMLLIElement;
        clone.setAttribute('data-ugc-section-clone', 'true');
        this.container?.appendChild(clone);
      });
    }

    this.items = Array.from(this.querySelectorAll('[data-ugc-section-ref="items"]'));
    this.resetHoverBuffers();
    this.bindItemTapListeners();
  }

  private handleResize(): void {
    if (!this.container || this.baseItems.length === 0) return;

    this.itemsGap = window.matchMedia('(min-width: 768px)').matches ? DESKTOP_GAP : MOBILE_GAP;
    this.syncItemsForViewport();

    if (this.items.length === 0) return;

    let left = 0;
    this.itemsCache = [];

    const firstItem = this.items[0];
    // offsetWidth/Height ignore CSS transforms (unlike getBoundingClientRect).
    this.container.style.height = `${firstItem.offsetHeight}px`;
    const firstMedia = this.getMediaForItem(firstItem);
    this.mediaContentWidth = firstMedia?.offsetWidth ?? 0;
    this.mediaContentHeight = firstMedia?.offsetHeight ?? 0;
    this.maxLabelShiftX = -((this.mediaContentWidth * (HOVER_SCALE - 1)) / 2);
    this.maxLabelShiftY = (this.mediaContentHeight * (HOVER_SCALE - 1)) / 2;

    this.items.forEach((item) => {
      const width = item.offsetWidth;

      this.itemsCache.push({
        width,
        left: left + this.itemsGap,
      });

      left += width + this.itemsGap;
    });

    this.itemContentWidth = this.itemsCache[0]?.width ?? 0;
    this.itemWidth = this.itemContentWidth + this.itemsGap;
    this.isAnimated = this.items.length * this.itemWidth > window.innerWidth;
    this.resetHoverBuffers();
  }

  private getItemBaseX(index: number, totalItemsWidth: number): number {
    const itemCache = this.itemsCache[index];
    if (!itemCache) return 0;

    if (!this.isAnimated || totalItemsWidth === 0) {
      return itemCache.left;
    }

    let x = this.currentDragX + itemCache.left;
    return (((x % totalItemsWidth) + totalItemsWidth) % totalItemsWidth) - this.itemWidth;
  }

  private getActiveItemIndex(baseXs: number[]): number {
    if (!this.container || baseXs.length === 0) return -1;

    const containerLeft = this.container.getBoundingClientRect().left;
    let closestIndex = -1;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index < baseXs.length; index += 1) {
      const width = this.itemsCache[index]?.width ?? this.itemContentWidth;
      const centerX = containerLeft + baseXs[index] + width / 2;
      const distance = Math.abs(this.mouseX - centerX);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    return closestIndex;
  }

  private getSignedLoopDistance(fromX: number, toX: number, loopWidth: number): number {
    if (loopWidth <= 0) return toX - fromX;
    let delta = toX - fromX;
    delta = ((delta + loopWidth / 2) % loopWidth) - loopWidth / 2;
    return delta;
  }

  private onUpdate(timestamp: number): void {
    if (!this.container || this.items.length === 0) return;

    const prefersReducedMotion = this.reducedMotionMediaQuery.matches;
    const canDockHover = this.usesDockHover && !prefersReducedMotion;
    const totalItemsWidth = this.itemWidth * this.items.length;
    const push = (this.itemContentWidth * HOVER_SCALE - this.itemContentWidth) / 2;

    if (this.isAnimated) {
      if (!this.isDragging && !this.isHovered && !prefersReducedMotion) {
        const deltaTime = Math.min(timestamp - this.lastFrameTime, 100);
        this.targetDragX += (this.autoScrollSpeed * this.autoScrollDirection * deltaTime) / 16.67;
      }

      this.currentDragX += (this.targetDragX - this.currentDragX) * this.dragXEase;
    }

    const baseXs = this.items.map((_, index) => this.getItemBaseX(index, totalItemsWidth));
    const activeIndex = canDockHover && this.isHovered && !this.isDragging ? this.getActiveItemIndex(baseXs) : -1;

    this.items.forEach((item, index) => {
      const itemCache = this.itemsCache[index];
      if (!itemCache) return;

      const targetScale = activeIndex === index ? HOVER_SCALE : 1;
      let targetShift = 0;

      if (activeIndex >= 0) {
        const activeX = baseXs[activeIndex];
        const signedDistance = this.getSignedLoopDistance(activeX, baseXs[index], totalItemsWidth);
        if (signedDistance < 0) targetShift = -push;
        if (signedDistance > 0) targetShift = push;
      }

      this.mediaScale[index] =
        (this.mediaScale[index] ?? 1) + (targetScale - (this.mediaScale[index] ?? 1)) * HOVER_EASE;
      this.hoverShift[index] =
        (this.hoverShift[index] ?? 0) + (targetShift - (this.hoverShift[index] ?? 0)) * HOVER_EASE;

      const x = baseXs[index] + this.hoverShift[index];
      const scale = this.mediaScale[index];

      item.style.transform = `translate3d(${x}px, 0, 0)`;
      item.style.willChange = 'transform';
      item.style.zIndex = activeIndex === index ? '2' : '1';

      const media = this.getMediaForItem(item);
      if (media) {
        media.style.transform = `scale(${scale})`;
        media.style.transformOrigin = 'center center';
        media.style.willChange = 'transform';
      }

      const label = this.getLabelForItem(item);
      if (label) {
        const progress = (scale - 1) / (HOVER_SCALE - 1 || 1);
        const labelShiftX = this.maxLabelShiftX * progress;
        const labelShiftY = this.maxLabelShiftY * progress;
        label.style.transform = `translate3d(${labelShiftX}px, ${labelShiftY}px, 0)`;
        label.style.willChange = 'transform';
      }
    });

    this.lastFrameTime = timestamp;
    this.animationFrameId = requestAnimationFrame(this.onUpdateBound);
  }

  private onDragStart(event: MouseEvent | TouchEvent): void {
    this.isDragging = true;
    this.didDragThisGesture = false;
    this.pointerDownX = this.getClientX(event);
    this.pointerDownY = this.getClientY(event);
    this.startDragX = this.pointerDownX;
    this.container?.classList.remove('cursor-grab');
    this.container?.classList.add('cursor-grabbing');
  }

  private onDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;

    const clientX = this.getClientX(event);
    const clientY = this.getClientY(event);
    const deltaX = clientX - this.pointerDownX;
    const deltaY = clientY - this.pointerDownY;

    if (!this.didDragThisGesture && Math.hypot(deltaX, deltaY) > TAP_THRESHOLD_PX) {
      this.didDragThisGesture = true;
      this.hideFullItem();
    }

    if (!this.didDragThisGesture) return;

    const dragX = (this.startDragX - clientX) * -1 * this.dragXSpeed;
    this.targetDragX += dragX;
    this.startDragX = clientX;
  }

  private onDragEnd(): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.container?.classList.remove('cursor-grabbing');
    this.container?.classList.add('cursor-grab');
  }

  private onWheel(event: WheelEvent): void {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const dragX = event.deltaX * this.scrollXSpeed * -1;
    this.targetDragX += dragX;
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    if (event instanceof MouseEvent) {
      return event.clientX;
    }

    if (event.touches.length > 0) {
      return event.touches[0].clientX;
    }

    if (event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }

    return this.startDragX;
  }

  private getClientY(event: MouseEvent | TouchEvent): number {
    if (event instanceof MouseEvent) {
      return event.clientY;
    }

    if (event.touches.length > 0) {
      return event.touches[0].clientY;
    }

    if (event.changedTouches.length > 0) {
      return event.changedTouches[0].clientY;
    }

    return this.pointerDownY;
  }
}

customElements.define('ugc-section', UgcSection);
